import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { MapPin, ChevronRight, ChevronLeft } from 'lucide-react'
import { careerPhases } from '../data/portfolioData'
import { TIMELINE } from '../constants/testIds'

// Equirectangular projection
// ViewBox: 0 0 1200 680
// Lon range: 25°E → 165°E (140° wide)
// Lat range: 55°N → -50°S (105° tall)
const X = (lon) => ((lon - 25) / 140) * 1200
const Y = (lat) => ((55 - lat) / 105) * 680

// Verified city positions — must match careerPhases order: [Pune, Kuwait, Melbourne]
const CITIES = [
  { x: X(73.8), y: Y(18.5) },   // Phase 0 = Pune: 73.8°E 18.5°N
  { x: X(47.9), y: Y(29.4) },   // Phase 1 = Kuwait: 47.9°E 29.4°N
  { x: X(144.9), y: Y(-37.8) }, // Phase 2 = Melbourne: 144.9°E 37.8°S
]

export default function CareerTimeline() {
  const [active, setActive] = useState(0)

  return (
    <section id="timeline" data-testid={TIMELINE.section} className="py-16 md:py-24 bg-[#F3F4F6]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.5 }}
          className="flex items-center gap-3 mb-4">
          <span className="font-mono text-[#0047AB] text-xs tracking-[0.3em] uppercase">Career Journey</span>
          <span className="h-px flex-1 max-w-[60px] bg-[#0047AB]/25" />
        </motion.div>

        <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.05 }}
          className="font-sans font-bold text-4xl md:text-5xl tracking-tight text-[#111111] mb-3">
          Three countries.
          <br /><span className="text-[#6B7280]">One direction.</span>
        </motion.h2>

        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
          viewport={{ once: true }} transition={{ delay: 0.1 }}
          className="font-body text-[#6B7280] text-base max-w-xl mb-8">
          From enterprise helpdesk in India, to critical national infrastructure in Kuwait, to full-stack implementation in Melbourne.
        </motion.p>

        {/* World Map */}
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
          viewport={{ once: true }} transition={{ duration: 0.8 }}
          className="mb-8">
          <WorldMap phases={careerPhases} active={active} onSelect={setActive} />
        </motion.div>

        {/* Phase tabs */}
        <div className="flex flex-wrap gap-2 mb-5">
          {careerPhases.map((phase, i) => (
            <button key={phase.id} onClick={() => setActive(i)}
              data-testid={`timeline-phase-${phase.id}`}
              className={`px-4 py-2 rounded-lg border text-sm font-mono transition-all duration-200 ${
                active === i
                  ? 'border-[#0047AB] bg-[#EBF2FF] text-[#0047AB]'
                  : 'border-[#E5E7EB] bg-white text-[#6B7280] hover:border-[#0047AB]/30'
              }`}>
              {phase.phase} — {phase.city}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <PhaseDetail key={active} phase={careerPhases[active]} />
        </AnimatePresence>
      </div>
    </section>
  )
}

function WorldMap({ phases, active, onSelect }) {
  return (
    <div className="w-full overflow-hidden rounded-2xl border border-[#E5E7EB] shadow-card">
      <svg viewBox="0 0 1200 680" className="w-full" aria-label="Career journey world map" style={{ display: 'block' }}>
        {/* Ocean */}
        <rect width="1200" height="680" fill="#EBF4FF" />

        {/* Latitude reference lines */}
        {[30, 20, 10, 0, -10, -20, -30, -40].map(lat => (
          <line key={lat} x1={0} y1={Y(lat)} x2={1200} y2={Y(lat)}
            stroke="#C7DCF5" strokeWidth="0.6" strokeDasharray={lat === 0 ? "none" : "4 8"} opacity={lat === 0 ? 0.6 : 0.4} />
        ))}
        {/* Equator label */}
        <text x="8" y={Y(0) - 4} fontSize="8" fill="#93BBDE" style={{ fontFamily: 'JetBrains Mono, monospace' }}>EQUATOR</text>

        {/* Longitude reference lines */}
        {[50, 75, 100, 125, 150].map(lon => (
          <line key={lon} x1={X(lon)} y1={0} x2={X(lon)} y2={680}
            stroke="#C7DCF5" strokeWidth="0.5" strokeDasharray="4 8" opacity="0.35" />
        ))}

        {/* === CONTINENT SHAPES === */}

        {/* Eastern Africa / Horn of Africa (left edge) */}
        <path d="M 0,180 L 52,168 L 86,180 L 103,205 L 100,233 L 86,270 L 90,320 L 83,360 L 68,400 L 43,430 L 17,440 L 0,420 Z"
          fill="#E8ECF1" stroke="#C8D0DC" strokeWidth="1.2" />

        {/* Arabian Peninsula — realistic shape */}
        <path d="
          M 86,168 L 111,160 L 154,161 L 196,168
          C 210,170 215,175 222,180
          L 240,192 L 258,213
          C 265,220 264,225 258,228
          L 248,232 L 236,245
          C 228,258 218,268 205,278
          L 188,285 L 171,290 L 155,288
          L 138,282 L 126,270 L 118,255
          C 110,242 105,232 103,222
          L 100,205 Z"
          fill="#E8ECF1" stroke="#C8D0DC" strokeWidth="1.3" />

        {/* Iraq / Mesopotamia hint */}
        <path d="M 171,140 L 214,132 L 240,145 L 248,160 L 222,168 L 196,168 L 171,160 Z"
          fill="#E8ECF1" stroke="#C8D0DC" strokeWidth="0.8" />

        {/* Iran / Persia */}
        <path d="M 240,140 L 300,128 L 360,132 L 385,148 L 380,168 L 360,178 L 330,180 L 300,175 L 270,168 L 248,160 L 240,145 Z"
          fill="#E8ECF1" stroke="#C8D0DC" strokeWidth="0.9" />

        {/* Pakistan */}
        <path d="M 300,175 L 360,178 L 385,148 L 420,152 L 450,160 L 465,175 L 454,188 L 420,195 L 386,198 L 343,200 L 320,188 Z"
          fill="#E8ECF1" stroke="#C8D0DC" strokeWidth="0.9" />

        {/* Indian Subcontinent — detailed realistic shape */}
        <path d="
          M 343,200 L 386,198 L 420,195 L 454,188 L 471,198
          L 514,185 L 557,200
          C 563,205 564,215 560,225
          L 554,248 L 546,268
          C 540,280 530,293 518,305
          L 499,318 L 479,328
          C 467,334 458,335 452,332
          L 440,325 L 430,318
          C 419,308 411,298 407,285
          L 400,268 L 394,250
          C 391,240 390,230 393,220
          L 398,208 L 395,200
          C 388,196 372,196 360,198
          L 343,200 Z"
          fill="#E8ECF1" stroke="#C8D0DC" strokeWidth="1.5" />

        {/* Sri Lanka */}
        <ellipse cx="462" cy="342" rx="14" ry="18" fill="#E8ECF1" stroke="#C8D0DC" strokeWidth="1" />

        {/* Bangladesh / Myanmar coast */}
        <path d="M 557,200 L 580,196 L 610,205 L 625,220 L 620,240 L 600,248 L 580,245 L 560,235 L 554,248 Z"
          fill="#E8ECF1" stroke="#C8D0DC" strokeWidth="0.8" />

        {/* Indochina Peninsula (Thailand, Vietnam, Cambodia) */}
        <path d="
          M 600,175 L 643,168 L 686,175 L 714,195
          L 720,220 L 715,248
          C 711,263 703,275 694,285
          L 683,295 L 671,318
          C 667,330 664,342 662,355
          L 655,368 L 648,375
          C 643,380 636,382 631,378
          L 620,365 L 615,345
          C 611,328 612,310 617,295
          L 623,268 L 618,242
          C 615,228 610,216 608,205
          L 600,175 Z"
          fill="#E8ECF1" stroke="#C8D0DC" strokeWidth="1" />

        {/* Malay Peninsula */}
        <path d="M 648,375 L 655,368 L 662,355 L 671,375 L 674,400 L 672,420 L 665,435 L 654,435 L 648,415 L 645,395 Z"
          fill="#E8ECF1" stroke="#C8D0DC" strokeWidth="0.9" />

        {/* Sumatra */}
        <path d="
          M 620,365 L 648,348 L 685,338 L 717,342 L 742,360
          L 748,382 L 738,408 L 715,422 L 688,425
          L 658,415 L 636,398 L 620,378 Z"
          fill="#E8ECF1" stroke="#C8D0DC" strokeWidth="0.9" />

        {/* Borneo */}
        <path d="
          M 729,295 L 762,278 L 800,278 L 825,292
          L 842,318 L 840,352 L 825,380
          L 800,395 L 768,395 L 742,375
          L 728,348 L 725,318 Z"
          fill="#E8ECF1" stroke="#C8D0DC" strokeWidth="1" />

        {/* Java */}
        <path d="M 692,415 L 725,405 L 762,408 L 800,415 L 815,428 L 788,440 L 748,442 L 712,435 Z"
          fill="#E8ECF1" stroke="#C8D0DC" strokeWidth="0.7" />

        {/* Sulawesi hint */}
        <path d="M 840,310 L 862,298 L 875,312 L 870,338 L 855,350 L 842,342 Z"
          fill="#E8ECF1" stroke="#C8D0DC" strokeWidth="0.7" />

        {/* New Guinea */}
        <path d="
          M 935,380 L 975,365 L 1028,368 L 1065,380
          L 1075,398 L 1062,415
          L 1028,420 L 988,418 L 955,408 L 935,395 Z"
          fill="#E8ECF1" stroke="#C8D0DC" strokeWidth="1" />

        {/* AUSTRALIA — detailed realistic outline */}
        <path d="
          M 763,500 C 775,490 795,482 815,472
          L 860,455 L 900,445 L 938,432
          C 950,428 962,432 975,438
          L 995,445 L 1020,435 L 1028,425
          C 1035,420 1038,425 1038,432
          L 1028,455 L 1042,480
          L 1071,512 L 1085,545
          C 1090,558 1088,572 1083,583
          L 1078,596 L 1071,610
          C 1065,618 1055,622 1045,618
          L 1028,608 L 1002,600
          L 968,596 L 935,590
          C 912,586 888,583 865,583
          L 820,578 C 798,572 778,560 763,545
          C 754,534 751,520 756,508 Z"
          fill="#E8ECF1" stroke="#C8D0DC" strokeWidth="1.5" />

        {/* Tasmania */}
        <ellipse cx="1052" cy="630" rx="22" ry="15" fill="#E8ECF1" stroke="#C8D0DC" strokeWidth="1" />

        {/* Philippine Islands hint */}
        <path d="M 832,232 L 848,218 L 862,222 L 858,242 L 845,252 L 831,248 Z"
          fill="#E8ECF1" stroke="#C8D0DC" strokeWidth="0.7" />

        {/* CONNECTING PATH — chronological: Pune → Kuwait → Melbourne */}
        <AnimatedConnector />

        {/* CITY NODES */}
        {phases.map((phase, i) => {
          const { x, y } = CITIES[i]
          const isActive = active === i
          return (
            <g key={phase.id} onClick={() => onSelect(i)} style={{ cursor: 'pointer' }}>
              {/* Pulse ring */}
              {isActive && (
                <motion.circle cx={x} cy={y} r="18" fill="none" stroke="#0047AB" strokeWidth="1.5"
                  initial={{ opacity: 0.8, scale: 1 }} animate={{ opacity: 0, scale: 2 }}
                  transition={{ duration: 1.8, repeat: Infinity }} />
              )}
              {/* Shadow/glow */}
              <circle cx={x} cy={y} r="12" fill={isActive ? 'rgba(0,71,171,0.12)' : 'rgba(0,0,0,0.04)'} />
              {/* Outer ring */}
              <circle cx={x} cy={y} r="8"
                fill={isActive ? '#EBF2FF' : 'white'}
                stroke={isActive ? '#0047AB' : '#9CA3AF'}
                strokeWidth={isActive ? 2 : 1.2}
                className="transition-all duration-300" />
              {/* Inner dot */}
              <circle cx={x} cy={y} r="3.5"
                fill={isActive ? '#0047AB' : '#D1D5DB'}
                className="transition-all duration-300" />
              {/* City label */}
              <text x={x} y={y - 15} textAnchor="middle"
                fontSize="8.5" fontWeight={isActive ? "600" : "400"}
                fill={isActive ? '#0047AB' : '#6B7280'}
                style={{ fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.08em' }}>
                {phase.city.toUpperCase()}
              </text>
              {/* Year label */}
              <text x={x} y={y + 22} textAnchor="middle"
                fontSize="7" fill={isActive ? '#0047AB' : '#9CA3AF'}
                style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {phase.years}
              </text>
            </g>
          )
        })}

        {/* Country region labels */}
        <text x={X(50)} y={Y(24)} textAnchor="middle" fontSize="7.5" fill="#9CA3AF"
          style={{ fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em' }}>SAUDI ARABIA</text>
        <text x={X(78)} y={Y(22)} textAnchor="middle" fontSize="7.5" fill="#9CA3AF"
          style={{ fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em' }}>INDIA</text>
        <text x={X(134)} y={Y(-25)} textAnchor="middle" fontSize="8" fill="#9CA3AF"
          style={{ fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em' }}>AUSTRALIA</text>
        <text x={X(108)} y={Y(5)} textAnchor="middle" fontSize="7" fill="#AEBCCC"
          style={{ fontFamily: 'JetBrains Mono, monospace' }}>INDIAN OCEAN</text>
      </svg>
    </div>
  )
}

function AnimatedConnector() {
  // Path: Pune → Kuwait (westward arc) → Melbourne (eastward sweep)
  const px = X(73.8), py = Y(18.5)    // Pune
  const kx = X(47.9), ky = Y(29.4)   // Kuwait
  const mx = X(144.9), my = Y(-37.8) // Melbourne

  const path = `M ${px},${py} C ${px - 80},${py - 60} ${kx + 60},${ky + 30} ${kx},${ky} C ${kx - 30},${ky + 80} ${mx - 200},${my - 100} ${mx},${my}`

  return (
    <>
      {/* Static background path */}
      <path d={path} fill="none" stroke="#0047AB" strokeWidth="1.2" strokeOpacity="0.18" strokeDasharray="5 7" />
      {/* Animated travelling dot */}
      <motion.circle r="4" fill="#0047AB" opacity="0.85"
        animate={{ offsetDistance: ['0%', '100%'], opacity: [0, 1, 1, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', times: [0, 0.08, 0.92, 1] }}
        style={{ offsetPath: `path("${path}")` }}
      />
    </>
  )
}

function PhaseDetail({ phase }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className="border border-[#E5E7EB] rounded-2xl bg-white shadow-card p-6 md:p-7"
    >
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="font-mono text-[#0047AB] text-xs tracking-[0.3em] uppercase mb-1">{phase.phase}</div>
          <h3 className="font-sans font-bold text-2xl text-[#111111]">{phase.city}, {phase.country}</h3>
          <div className="font-mono text-[#9CA3AF] text-xs mt-0.5">{phase.years}</div>
        </div>
        <div className="flex items-center gap-2 text-[#6B7280] text-sm font-body">
          <MapPin size={13} className="text-[#0047AB]" />
          {phase.tagline}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {phase.roles.map((role, i) => (
          <div key={i} className="p-4 border border-[#E5E7EB] rounded-xl bg-[#FAFAFA] hover:border-[#0047AB]/20 transition-colors duration-200">
            <div className="font-sans font-semibold text-[#111111] text-sm mb-0.5">{role.title}</div>
            <div className="font-body text-[#6B7280] text-xs mb-3">{role.company} · {role.period}</div>
            <ul className="space-y-1.5">
              {role.highlights.map((h, j) => (
                <li key={j} className="flex items-start gap-2 text-[#6B7280] text-xs font-body">
                  <ChevronRight size={9} className="text-[#0047AB] mt-0.5 shrink-0" />
                  {h}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </motion.div>
  )
}
